import API from '@/api/api';
import {computed, type ComputedRef, ref} from 'vue';
import {computedAsync, until} from '@vueuse/core';
import {defineStore, storeToRefs} from 'pinia';
import {useUserStore} from './userStore';
import {parseZodSchema} from '@/api/models/parseZodSchema';
import {
    featureFlagResponseModel,
    type FeatureFlagResponse,
    type LicensedFeatureFlag,
    type ValidLicense,
} from '@/api/models/featureFlags/featureFlagModel';
import type {FeatureFlagName} from '@/api/models/featureFlags/featureFlagName';

export const useFeatureFlagStore = defineStore('featureFlags', () => {
    const {useFeatureFlag} = useHierarchicalFeatureFlags();

    const sendNotifications = useFeatureFlag('send-notifications');
    const receiveNotifications = useFeatureFlag('receive-notifications');

    //booking flags
    const bookingFilter = useFeatureFlag('booking-filter');
    const bookingPresentUsers = useFeatureFlag('booking-present-users');
    const bookingResourceList = useFeatureFlag('booking-resource-list');
    const bookingResourceListStreaming = useFeatureFlag('booking-resource-list-streaming');
    const bookingDetailPage = useFeatureFlag('booking-detail-page');
    const bookingQueue = useFeatureFlag('booking-queue');
    const bookingCalendarCollapse = useFeatureFlag('booking-calendar-collapse');
    const bookingComments = useFeatureFlag('booking-comments');
    const bookingSpaceAvailabilities = useFeatureFlag('booking-space-availabilities');

    const favoriteSeatsPage = useFeatureFlag('favorite-seats-page');
    const newNavigation = useFeatureFlag('new-booking');
    const kioskMode = useFeatureFlag('kiosk-mode');
    const kioskModeCheckIn = useFeatureFlag('kiosk-mode-check-in');
    const calendarUserSubscriptions = useFeatureFlag('calendar-user-subscriptions');
    const outlookCalendarSync = useFeatureFlag('calendar-sync');
    const delegatedCalendarAccess = useFeatureFlag('delegated-calendar-access');
    const restApi = useFeatureFlag('rest-api');
    const mercure = useFeatureFlag('mercure');
    const desklyConnect = useFeatureFlag('deskly-connect');
    const desklyConnectAutoBooking = useFeatureFlag('deskly-connect-auto-booking');
    const desklyConnectLiveStatus = useFeatureFlag('deskly-connect-live-status');
    const desklyConnectUsageStatistics = useFeatureFlag('deskly-connect-usage-statistics');
    const auditLogAccess = useFeatureFlag('audit-log-access');
    const weekOverview = useFeatureFlag('week-overview');
    const celloReferralAccess = useFeatureFlag('cello');
    const occupancyAccess = useFeatureFlag('occupancy');
    const bookingRuleRestriction = useFeatureFlag('booking-rule-restrictions');
    const bookingRulePriority = useFeatureFlag('booking-rule-priorities');
    const bookingRulePermanent = useFeatureFlag('booking-rule-permanent');
    const bookingRules = useCombinedAnyFeatureFlags(
        bookingRulePriority,
        bookingRulePermanent,
        bookingRuleRestriction
    );
    const markerSizing = useFeatureFlag('marker-sizing');
    const spaceImages = useFeatureFlag('space-images');
    const parkingSpaceResource = useFeatureFlag('parking-spaces');
    const aiAssistant = useFeatureFlag('ai-assistant');
    const licensePlates = useFeatureFlag('license-plates');
    const userBookingSummary = useFeatureFlag('user-booking-summary');
    const userTags = useFeatureFlag('user-tags');
    const userTagsCustom = useFeatureFlag('user-tags-custom');
    const resourceConversion = useFeatureFlag('resource-conversion');
    const cancelledBooking = useFeatureFlag('cancelled-bookings');
    const licenseSelfService = useFeatureFlag('license-self-service');
    const customTimeframes = useFeatureFlag('custom-timeframes');
    const newUserAdministration = useFeatureFlag('new-user-administration');
    const suppressionListRemoval = useFeatureFlag('suppression-list-removal');
    const selectionInfoBanner = useFeatureFlag('selection-info-banner');
    const analyticsSensorStats = useFeatureFlag('deskly-connect-analytics-sensor-stats');
    const occupancySensorStats = useFeatureFlag('deskly-connect-occupancy-sensor-stats');
    const separatedBookingRules = useFeatureFlag('separated-booking-rules');
    const permissionGroupCheckin = useFeatureFlag('permission-group-checkin');
    const featurebase = useFeatureFlag('featurebase');
    const featurebaseFeedbackWidget = useFeatureFlag('featurebase-feedback-widget');
    const guestBookings = useFeatureFlag('guest-bookings');
    const registrationMailToFirstAdmin = useFeatureFlag('registration-mail-to-first-admin');
    const subdomainRedirect = useFeatureFlag('subdomain-redirect');
    const newBookingAnonymization = useFeatureFlag('new-booking-anonymization');
    const superAdminAccess = useFeatureFlag('super-admin-access');
    const teamsMessenger = useFeatureFlag('teams-messenger');

    return {
        sendNotifications,
        receiveNotifications,

        //booking flags
        bookingFilter,
        bookingPresentUsers,
        bookingResourceList,
        bookingResourceListStreaming,
        bookingDetailPage,
        bookingQueue,
        bookingCalendarCollapse,
        bookingSpaceAvailabilities,

        favoriteSeatsPage,
        newNavigation,
        kioskMode,
        kioskModeCheckIn,
        calendarUserSubscriptions,
        outlookCalendarSync,
        delegatedCalendarAccess,
        restApi,
        mercure,
        desklyConnect,
        desklyConnectAutoBooking,
        desklyConnectLiveStatus,
        desklyConnectUsageStatistics,
        auditLogAccess,
        weekOverview,
        celloReferralAccess,
        occupancyAccess,
        bookingRules,
        bookingRuleRestriction,
        bookingRulePriority,
        bookingRulePermanent,
        markerSizing,
        spaceImages,
        parkingSpaceResource,
        aiAssistant,
        licensePlates,
        userBookingSummary,
        bookingComments,
        userTags,
        userTagsCustom,
        resourceConversion,
        cancelledBooking,
        licenseSelfService,
        newUserAdministration,
        customTimeframes,
        selectionInfoBanner,
        suppressionListRemoval,
        analyticsSensorStats,
        occupancySensorStats,
        separatedBookingRules,
        permissionGroupCheckin,
        featurebase,
        featurebaseFeedbackWidget,
        guestBookings,
        registrationMailToFirstAdmin,
        subdomainRedirect,
        newBookingAnonymization,
        superAdminAccess,
        teamsMessenger,
    };
});

export type FeatureFlagState = {
    loading: boolean;
    isActive: boolean;
    isReady: () => Promise<boolean>;
    availableInLicenseTiers: ValidLicense[] | null;
};

function useHierarchicalFeatureFlags() {
    const {featureFlagResponse, isLoading} = useFeatureFlagResponse();

    function useFeatureFlag(flagName: FeatureFlagName): ComputedRef<FeatureFlagState> {
        const featureFlag = computed<LicensedFeatureFlag>(
            () => featureFlagResponse.value[flagName] ?? null
        );

        const isActive = computed<boolean>(
            () => !isLoading.value && featureFlag.value !== false && featureFlag.value !== null
        );
        const availableInLicenseTiers = computed<ValidLicense[] | null>(() => {
            const flag = featureFlag.value;
            if (Array.isArray(flag)) {
                return flag;
            }
            return null;
        });

        async function isReady(): Promise<boolean> {
            await until(isLoading).toBe(false);
            return isActive.value;
        }

        return computed<FeatureFlagState>(() => ({
            isActive: isActive.value ?? false,
            loading: isLoading.value,
            isReady,
            availableInLicenseTiers: availableInLicenseTiers.value,
        }));
    }

    return {
        useFeatureFlag,
    };
}

/**
 * Combines multiple feature flags into one, requiring at least one to be active.
 * The resulting feature flag state will hold the lowest license tier to make any available.
 */
function useCombinedAnyFeatureFlags(...featureFlags: ComputedRef<FeatureFlagState>[]) {
    const isActive = computed(() => featureFlags.some(flag => flag.value.isActive));
    const loading = computed(() => featureFlags.some(flag => flag.value.loading));

    const availableInLicenseTiers = computed<ValidLicense[] | null>(() => {
        const licenseTiers = featureFlags
            .map(flag => flag.value.availableInLicenseTiers)
            .filter((tiers): tiers is ValidLicense[] => tiers !== null);

        if (licenseTiers.length !== featureFlags.length) {
            return null;
        }

        return Array.from(new Set(licenseTiers.flat()));
    });

    return computed<FeatureFlagState>(() => ({
        isActive: isActive.value,
        loading: loading.value,
        isReady: async () => {
            await until(loading).toBe(false);
            return isActive.value;
        },
        availableInLicenseTiers: availableInLicenseTiers.value,
    }));
}

function useFeatureFlagResponse() {
    const {isAuthenticated, user} = storeToRefs(useUserStore());

    const isLoading = ref(true);
    const featureFlagResponse = computedAsync(
        () => {
            if (!isAuthenticated.value || !user.value?.id) {
                return {} as FeatureFlagResponse;
            }
            // TODO Use TanStack query as soon as available
            return API.get('/api/unleash', true, true)
                .then(API.handle)
                .then(data => parseZodSchema(data, featureFlagResponseModel))
                .catch(() => ({}) as FeatureFlagResponse);
        },
        {},
        isLoading
    );

    return {featureFlagResponse, isLoading};
}
